import React, { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import DataTable from "../ui/DataTable";
import ConfirmDialog from "../ui/ConfirmDialog";
import { customizationService } from "../../services/customizationService";
import toast from "react-hot-toast";

const FIELD_TYPES = [
  { value: "string", label: "Text" },
  { value: "long_text", label: "Long text" },
  { value: "textarea", label: "Textarea" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiselect", label: "Multi-select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "date", label: "Date" },
  { value: "user", label: "User" },
  { value: "number", label: "Number" },
  { value: "url", label: "URL" },
  { value: "document", label: "Document" },
];

const ResultFieldsTab = ({ clientId }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("string");
  const [description, setDescription] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [options, setOptions] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadFields = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await customizationService.resultFieldsList({
        client_id: clientId,
      });
      setFields(res?.data?.result_fields || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load result fields");
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const needsOptions =
    type === "dropdown" || type === "multiselect" || type === "multi_select";

  const openCreate = () => {
    setEditId(null);
    setName("");
    setType("string");
    setDescription("");
    setIsRequired(false);
    setOptions("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setName(row.name || "");
    setType(row.type || "string");
    setDescription(row.description || "");
    setIsRequired(!!row.is_required);
    const opts = row.options;
    if (Array.isArray(opts)) {
      setOptions(
        opts
          .map((o) => (typeof o === "string" ? o : o.value))
          .filter(Boolean)
          .join("\n"),
      );
    } else {
      setOptions("");
    }
    setModalOpen(true);
  };

  const parseOptions = (text) => {
    return text
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const handleSave = async () => {
    const n = name.trim();
    if (!n) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: n,
        type,
        description: description.trim() || null,
        is_required: isRequired,
        options: needsOptions
          ? parseOptions(options).map((v) => ({ value: v }))
          : undefined,
      };
      if (editId) {
        await customizationService.resultFieldsUpdate(editId, payload);
        toast.success("Result field updated");
      } else {
        await customizationService.resultFieldsCreate(payload);
        toast.success("Result field created");
      }
      setModalOpen(false);
      loadFields();
    } catch (e) {
      toast.error(
        e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setSelectedField(row);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedField) return;
    setDeleteLoading(true);
    try {
      await customizationService.resultFieldsDelete(selectedField.id);
      toast.success("Result field deleted");
      setDeleteDialog(false);
      setSelectedField(null);
      loadFields();
    } catch (e) {
      toast.error(
        e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          "Delete failed",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {row.is_required && (
            <Badge variant="outline" className="text-xs">
              Required
            </Badge>
          )}
          {row.is_system && <Badge variant="secondary">System</Badge>}
        </div>
      ),
    },
     {
      key: "type",
      header: "Type",
      render: (_, row) =>
        FIELD_TYPES.find((t) => t.value === row.type)?.label || row.type,
    },
    {
      key: "fieldType",
      header: "System/Custom",
      render: (_, row) =>row.is_system?"System":"Custom"
       
    },
    {
      key: "description",
      header: "Description",
      render: (_, row) => row.description || "—",
    },
    {
      key: "actions",
      header: "",
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
            <Pencil className="w-4 h-4" />
          </Button>
          {!row.is_system && (
            <Button
              variant="ghost"
              size="icon"
              className="text-red-600"
              onClick={() => openDelete(row)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-gray-600 text-sm mt-1">
            Configure result-level fields for test execution (e.g. Comments,
            Time Spent, Defects).
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Add Result Field
        </Button>
      </div>

      <div className="card overflow-hidden">
        <DataTable
          data={fields}
          columns={columns}
          loading={loading}
          emptyMessage="No result fields. Add one above."
        />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editId ? "Edit Result Field" : "Add Result Field"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Execution Comments"
                disabled={
                  !!editId && fields.find((f) => f.id === editId)?.is_system
                }
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={type}
                onValueChange={setType}
                disabled={
                  !!editId && fields.find((f) => f.id === editId)?.is_system
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rf-required"
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
              />
              <Label htmlFor="rf-required">Required</Label>
            </div>
            {needsOptions && (
              <div>
                <Label>Options (one per line or comma-separated)</Label>
                <textarea
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={options}
                  onChange={(e) => setOptions(e.target.value)}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setSelectedField(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Result Field"
        message={`Are you sure you want to delete "${selectedField?.name}"? Existing execution values for this field may be lost.`}
        confirmText="Delete"
        confirmVariant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ResultFieldsTab;
